import styled, { css } from 'styled-components';
import React, { HTMLAttributes } from 'react';
import { IconButton } from '../../../../../shared/components';

export const RelativeWrapper = styled(({ ...restProps }) => <div {...restProps} />)`
  position: relative;
  &.is-empty {
    min-height: 10vh;
  }
`;

export const ContentWrapper = styled(({ isMobile, bookmarkMode, isBookmarked, ...restProps }) => (
  <div {...restProps} />
))`
  margin-left: ${({ isMobile, bookmarkMode, isBookmarked }) =>
    isMobile && (bookmarkMode || isBookmarked) ? '2rem' : 0};
`;

export const WhiteContentWrapper = styled(
  ({ isMobile, bookmarkMode, isBookmarked, ...restProps }) => <div {...restProps} />
)`
  background-color: #fff;
  border-radius: ${({ theme }) => theme.config.defaultBorderRadius};
  border: 1px solid ${({ theme }) => theme.config.appbarColors.borderColor};
  margin-left: ${({ isMobile, bookmarkMode, isBookmarked }) =>
    isMobile && (bookmarkMode || isBookmarked) ? '2rem' : 0};
`;

export const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 1.6rem;
  border-bottom: ${({ theme }) => `.1rem solid ${theme.palette.grey['100']}`};
`;

export const HoverBlockWrapper = styled(({ studyGuide, ...restProps }) => <div {...restProps} />)`
  top: ${({ studyGuide }) => (studyGuide ? '0' : '-2rem')};
  right: ${({ studyGuide }) => (studyGuide ? '-3.1rem' : '-2rem')};
  bottom: ${({ studyGuide }) => (studyGuide ? '0' : '-2rem')};
  left: ${({ studyGuide }) => (studyGuide ? '0' : '-2rem')};
  border: ${({ studyGuide, theme }) =>
    studyGuide ? 'none' : `2px dashed #0092BC`}; // add this to theme
  position: absolute;
`;

export const HoverBlockTitle = styled.div`
  position: absolute;
  background: #ffffff;
  // padding: 0.1rem 0.5rem; // 0.6rem 1rem;
  top: 0;
  left: -0.1rem;
  transform: translateY(-50%);
  font-size: 1.4rem;
  border: ${({ theme }) => `1px solid ${theme.palette.ternary.main}`};
  box-shadow: 0 0 1rem -0.2rem ${({ theme }) => theme.palette.grey['400']};
  color: ${({ theme }) => theme.palette.grey['900']};
`;

export const HoverBlockIconButton = styled(({ transform, ...restProps }) => (
  <IconButton {...restProps} />
))`
  position: absolute;
  top: 0;
  right: -0.1rem;
  transform: ${({ transform }) => transform ?? 'translateY(-50%)'};
  padding: 0.9rem;
  background: #ffffff;
  border-radius: 0;
  width: 3.2rem;
  height: 3.2rem;
  box-shadow: 0 0 1rem -0.2rem ${({ theme }) => theme.palette.grey['400']};
  &:hover {
    background: ${({ theme }) => theme.palette.grey['50']};
  }
`;

// STUDYGUIDE

export const StudyGuideWrapper = styled(({ mobile, ...restProps }) => <div {...restProps} />)`
  position: relative;
  display: ${({ mobile }) => (mobile ? 'block' : 'flex')};
  margin-bottom: ${({ mobile }) => mobile && '2rem'};
`;

export const studyGuideBaseColumnStyle = css`
  position: relative;
  padding: 1rem;
  background: white;
  border-right: 0.1rem solid ${({ theme }) => theme.palette.studyGuide.main};
  border-bottom: 0.1rem solid ${({ theme }) => theme.palette.studyGuide.main};

  & .fr-view ul {
    list-style: none;

    li {
      position: relative;
      padding-left: 1.25rem;

      &:before {
        content: '•';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        font-size: ${({ theme }) => theme.typography.body1.fontSize};
      }
    }
  }
`;

export interface WrapperProps extends HTMLAttributes<HTMLDivElement> {
  readOnly?: boolean;
  studyGuide?: boolean;
  mobile?: boolean;
}

export const RefColumn = React.forwardRef<HTMLDivElement, WrapperProps>(
  ({ mobile, ...restProps }, ref) => <div {...restProps} ref={ref} />
);

export const StudyGuideQuestionColumn = styled(RefColumn)`
  ${studyGuideBaseColumnStyle};
  padding-top: ${({ mobile }) => mobile && '3rem'};
  width: ${({ mobile }) => (mobile ? '100%' : '25%')};
  border-left: 1px solid ${({ theme }) => theme.palette.studyGuide.main};
  border-top: ${({ mobile, theme }) => mobile && `1px solid ${theme.palette.studyGuide.main}`};

  p:first-child {
    margin-top: 0;
  }
`;

export const StudyGuideConceptsColumn = styled(RefColumn)`
  ${studyGuideBaseColumnStyle};
  padding-top: ${({ mobile }) => mobile && '3rem'};
  width: ${({ mobile }) => (mobile ? '100%' : '25%')};
  border-left: ${({ mobile, theme }) => mobile && `1px solid ${theme.palette.studyGuide.main}`};
  background: ${({ theme }) => theme.palette.studyGuide.light};
`;

export const StudyGuideSummaryColumn = styled(RefColumn)`
  ${studyGuideBaseColumnStyle};
  padding-top: ${({ mobile }) => mobile && '3rem'};
  width: ${({ mobile }) => (mobile ? '100%' : '50%')};
  border-left: ${({ mobile, theme }) => mobile && `1px solid ${theme.palette.studyGuide.main}`};

  p:first-child {
    margin-top: 0;
  }
`;

export const RefSummaryColumn = React.forwardRef<HTMLDivElement>((props, ref) => {
  return <StudyGuideSummaryColumn {...props} ref={ref} />;
});

export const StudyGuideMobileTitle = styled.div`
  background: ${({ theme }) => theme.palette.studyGuide.main};
  color: #fff;
  position: absolute;
  top: -1px;
  left: 0;
  transform-origin: 0 0;
  text-transform: uppercase;
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.bodyLg.fontSize};
  padding: 0.2rem 1rem;
`;

export const ColumnContent = styled(({ halfScreen, ...props }) => <div {...props} />)`
  ${({ halfScreen }) =>
    halfScreen &&
    `
      overflow: scroll;
      ::-webkit-scrollbar {
        width: 2px;
        height: 5px;
      }
      
      ::-webkit-scrollbar-track {
        background-color: #f2f2f2;
      }
      
      ::-webkit-scrollbar-thumb {
        background-color: #ccc;
        border-radius: 5px;
      }
    `}
`;

export const EditorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const EditBlockCover = styled.div`
  position: absolute;
  top: 0rem;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.5);
  border: none;
`;

export const EditBlockText = styled.p`
  text-align: center;
  font-weight: bold;
  margin: 0;
`;

export const EditBlockButton = styled(IconButton)`
  width: 60px;
  height: 60px;
`;

export const EditBlockContentButton = styled(IconButton)`
  width: 30px;
  height: 30px;
`;

// COLUMN BLOCK

export const ColumnBlockWrapper = styled('div')`
  display: flex;
  ${(props) => props.theme.breakpoints.down('sm')} {
    display: block;
  }
  flex-direction: row;
  width: 100%;
  height: 100%;
  gap: 1rem;
`;

export const ColumnBlockCol = styled((props) => <div {...props} />)`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${(props) => `${props.width || '50'}%`};
  }
  height: 100%;
  position: relative;

  @media (max-width: 600px) {
    width: 100%;
    padding: 0 0 0 2rem;
  }
`;

export const EmptyBlockWrapper = styled('div')`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.5);
  border: ${({ theme }) => `1px dashed ${theme.palette.grey['600']}`};
`;
